import { useRequiredContext } from "@redotech/react-util/context";
import AnnouncementIcon3 from "@redotech/redo-icons/announcement-03.svg";
import AnnouncementIcon3Filled from "@redotech/redo-icons/announcement-03_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { MarketingPermissions } from "@redotech/redo-model/permissions";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import { useNavTreeNode } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext } from "react";
import { MarketingCloudStatusesContext } from "../../../../redo-suite/marketing-cloud";
import { ReviewsToReviewContext } from "../../../../services/marketing/reviews-to-review-service";
import { AnalyticsItems } from "./analytics";
import { AutomationsItems } from "./automations";
import { EmailSMSItems } from "./email-sms";
import { LoyaltyItems } from "./loyalty";
import { MarketingAgentItems } from "./marketing-agent";
import { OrderTrackingItems } from "./order-tracking";
import { RecoverItems } from "./recover";
import { ReviewsItems } from "./reviews";

export function useMarketingNavigation(): PageNavigationEntry | null {
  const { marketingCloudEnabled, emailSmsEnabled } = useRequiredContext(
    MarketingCloudStatusesContext,
  );

  const routes = useRequiredContext(MerchantRoutesContext);
  const pathRoot = routes.marketingBaseUrl;

  const reviewCount = useContext(ReviewsToReviewContext);

  const canViewMarketingSection = useHasPermission(
    MarketingPermissions.VIEW_MARKETING,
  );

  if (!canViewMarketingSection) {
    return null;
  }

  const hasNotification = reviewCount > 0;

  const primaryHref = routes.getMarketingHref({
    emailSmsEnabled,
    marketingCloudEnabled,
  });

  const primaryTab: LinkPrimaryTab = {
    id: "marketing",
    hasNotification,
    UnfilledIcon: AnnouncementIcon3,
    FilledIcon: AnnouncementIcon3Filled,
    type: "link",
    pathRoot,
    href: primaryHref,
    friendlyName: "Marketing",
  };

  const secondaryNavigationData: SecondaryNavigationData | undefined =
    marketingCloudEnabled
      ? {
          id: "marketing",
          name: "Marketing",
          NavContent: SecondaryMarketingSection,
        }
      : undefined;

  return { primaryTab, secondaryNavigationData };
}

const SecondaryMarketingSection = memo(function SecondaryMarketingSection() {
  const { id: nodeId, TreeContext } = useNavTreeNode({
    friendlyName: "Marketing",
  });

  return (
    <TreeContext.Provider value={nodeId}>
      <AnalyticsItems />
      <MarketingAgentItems />
      <AutomationsItems />
      <EmailSMSItems />
      <LoyaltyItems />
      <RecoverItems />
      <ReviewsItems />
      <OrderTrackingItems />
    </TreeContext.Provider>
  );
});
